import ConfirmModal from '../../../../core/components/ConfirmModal.tsx';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentType, mapDocumentTypeToGqlDocumentType } from '../../../constants.ts';
import { useToaster } from '../../../../core/components/Toast.tsx';
import { useMutation } from '@apollo/client';
import { REPROCESS_DOCUMENT } from '../../../queries.ts';

type ReprocessDocumentModalProps = {
    recordIds: string[];
    documentType: DocumentType;
    onClose: () => void;
    onSuccess: () => void;
    refetch?: () => Promise<unknown>;
};

const ReprocessDocumentModal: React.FC<ReprocessDocumentModalProps> = ({
    recordIds,
    documentType,
    onClose,
    onSuccess,
    refetch,
}) => {
    const { t } = useTranslation('assistance');
    const { publishToast } = useToaster();

    const [reprocessDocument] = useMutation(REPROCESS_DOCUMENT);
    const isBulk = recordIds.length > 1;
    const gqlDocumentType = mapDocumentTypeToGqlDocumentType[documentType];

    const onSubmit = async () => {
        try {
            await Promise.all(
                recordIds.map((recordId) =>
                    reprocessDocument({
                        variables: {
                            recordId,
                            documentType: gqlDocumentType,
                        },
                    })
                )
            );

            await refetch?.();

            onSuccess();
            onClose();

            publishToast({
                description: isBulk ? t('reprocessModal.reprocessBulkSuccess') : t('reprocessModal.reprocessSuccess'),
                status: 'success',
            });
        } catch (error) {
            console.error(error);

            publishToast({
                description: isBulk ? t('reprocessModal.reprocessBulkError') : t('reprocessModal.reprocessError'),
                status: 'error',
            });
        }
    };

    return (
        <ConfirmModal
            title={isBulk ? t('reprocessModal.bulkTitle') : t('reprocessModal.title')}
            labelConfirm={isBulk ? t('reprocessModal.confirmBulkButton') : t('reprocessModal.confirmButton')}
            labelCancel={t('reprocessModal.cancelButton')}
            onConfirm={onSubmit}
            onCancel={onClose}
            visible
        >
            <p>{isBulk ? t('reprocessModal.bulkMessage') : t('reprocessModal.message')}</p>
        </ConfirmModal>
    );
};

export default ReprocessDocumentModal;
